"use client";

import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  List,
  ListItem,
  Flex,
  useBreakpointValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useColorMode,
  Image,
  Link,
  ModalHeader,
  Spinner,
  Tag,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { useState, useRef, useEffect } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { getCurrentColor } from "../../constants";
import useHttp from "../../hooks/useHttp";

// Helper to extract attribute text value
const getAttr = (item, key) => item?.attributes?.[key]?.text?.[0] || null;

// Helper to get the display title (prefer title_v2)
const getDisplayTitle = (item) =>
  item?.attributes?.title_v2?.text?.[0] || item.title;

export default function SearchUI() {
  const desktopModal = useDisclosure();
  const mobileDrawer = useDisclosure();
  const location = useLocation();
  const { getSuggest, postSuggestClick } = useHttp();
  const [results, setResults] = useState([]);

  // Close search modal/drawer when route changes
  useEffect(() => {
    desktopModal.onClose();
    mobileDrawer.onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { colorMode } = useColorMode();
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);

  const handleResultClick = (item, index) => {
    postSuggestClick({
      query,
      product_id: item.id,
      product_uri: item.uri,
      result_position: index,
    });
  };

  useEffect(() => {
    if (!query) {
      setResults([]);
      setLoading(false);
      return;
    }

    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    setResults([]);
    const controller = new AbortController();
    setLoading(true);

    const handler = setTimeout(async () => {
      try {
        const res = await getSuggest("?q=" + query, {
          signal: controller.signal,
        });
        if (
          currentRequestId === requestIdRef.current &&
          !controller.signal.aborted
        ) {
          setResults(res.results || []);
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          if (currentRequestId === requestIdRef.current) {
            setResults([]);
          }
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 400);

    return () => {
      clearTimeout(handler);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const DesktopSearch = (
    <>
      {/* MINI SEARCH BUTTON IN NAVBAR */}
      <IconButton
        aria-label="Search"
        icon={<SearchIcon />}
        variant="ghost"
        color="white"
        onClick={() => {
          desktopModal.onOpen();
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
      />

      {/* DESKTOP OVERLAY — Animated (slideInTop) */}
      <Modal
        isOpen={desktopModal.isOpen}
        onClose={desktopModal.onClose}
        trapFocus={true}
        motionPreset="slideInTop"
        closeOnOverlayClick={true}
        size={"6xl"}
        scrollBehavior={"inside"}
      >
        <ModalOverlay bg="rgba(0,0,0,0.65)" backdropFilter="blur(4px)" />

        {/* 3/4 HEIGHT CONTENT BOX */}
        <ModalContent
          bg={getCurrentColor(colorMode, "whiteC.100", "grayC.1700")}
          h="75vh"
          mt="10vh"
          borderRadius="16px"
          overflow={"hidden"}
        >
          <ModalHeader pt="60px">
            <ModalCloseButton />
            {/* LARGE SEARCH BAR */}
            <Flex justify="center">
              <InputGroup>
                <InputLeftElement>
                  <SearchIcon color="gray.400" />
                </InputLeftElement>

                <Input
                  ref={inputRef}
                  placeholder="Search"
                  size="md"
                  fontSize="20px"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </InputGroup>
            </Flex>
          </ModalHeader>

          <ModalBody>
            {/* SUGGESTIONS */}
            {query && (
              <Box mx="auto" mt={6}>
                {loading && <Spinner display="block" mx="auto" />}
                <List spacing={1}>
                  {results.length === 0 ? (
                    <ListItem p={3} color="gray.500">
                      No results found
                    </ListItem>
                  ) : (
                    results.map((item, index) => (
                      <ListItem
                        key={item.id}
                        p={3}
                        borderBottom="1px solid #eee"
                        _hover={{
                          bg: getCurrentColor(
                            colorMode,
                            "gray.100",
                            "grayC.2000",
                          ),
                          cursor: "pointer",
                        }}
                      >
                        <Flex gap={4}>
                          <Image
                            src={item.images?.[0]?.uri || "/placeholder.svg"}
                            alt={getDisplayTitle(item)}
                            maxW={"72px"}
                            maxH={"72px"}
                            minW={"72px"}
                            minH={"72px"}
                            borderRadius={"8px"}
                            objectFit={"cover"}
                          />
                          <Flex
                            direction={"column"}
                            gap={2}
                            justifyContent={"center"}
                          >
                            <Link
                              to={item.uri}
                              as={RouterLink}
                              onClick={() => {
                                handleResultClick(item, index);
                                desktopModal.onClose();
                              }}
                            >
                              {getDisplayTitle(item)}
                            </Link>
                            <Wrap spacing={2}>
                              {getAttr(item, "caliber") && (
                                <WrapItem>
                                  <Tag
                                    size="sm"
                                    colorScheme="blue"
                                    variant="subtle"
                                  >
                                    {getAttr(item, "caliber")}
                                  </Tag>
                                </WrapItem>
                              )}
                              {getAttr(item, "bullet_weight_text") && (
                                <WrapItem>
                                  <Tag
                                    size="sm"
                                    colorScheme="green"
                                    variant="subtle"
                                  >
                                    {getAttr(item, "bullet_weight_text")}
                                  </Tag>
                                </WrapItem>
                              )}
                              {getAttr(item, "bullet_type_s") && (
                                <WrapItem>
                                  <Tag
                                    size="sm"
                                    colorScheme="purple"
                                    variant="subtle"
                                  >
                                    {getAttr(item, "bullet_type_s")}
                                  </Tag>
                                </WrapItem>
                              )}
                              {getAttr(item, "material") && (
                                <WrapItem>
                                  <Tag
                                    size="sm"
                                    colorScheme="orange"
                                    variant="subtle"
                                  >
                                    {getAttr(item, "material")}
                                  </Tag>
                                </WrapItem>
                              )}
                            </Wrap>
                          </Flex>
                        </Flex>
                      </ListItem>
                    ))
                  )}
                </List>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );

  const MobileSearch = (
    <>
      <IconButton
        aria-label="Search"
        icon={<SearchIcon />}
        variant="ghost"
        color="white"
        onClick={() => {
          mobileDrawer.onOpen();
          setTimeout(() => inputRef.current?.focus(), 60);
        }}
      />

      <Drawer
        placement="top"
        onClose={mobileDrawer.onClose}
        isOpen={mobileDrawer.isOpen}
        size="full"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader borderBottomWidth="1px">
            <Flex align="center" gap={3}>
              <InputGroup flex="1">
                <InputLeftElement>
                  <SearchIcon color="gray.400" />
                </InputLeftElement>

                <Input
                  placeholder="Search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </InputGroup>

              <IconButton
                aria-label="Close Search"
                icon={<CloseIcon boxSize={3} />}
                onClick={mobileDrawer.onClose}
                variant="ghost"
              />
            </Flex>
          </DrawerHeader>

          <DrawerBody>
            {query && (
              <List mt={3} spacing={1}>
                {loading && <Spinner display="block" mx="auto" />}
                {results.length === 0 ? (
                  <ListItem p={3} color="gray.500">
                    No results found
                  </ListItem>
                ) : (
                  results.map((item, index) => (
                    <ListItem
                      key={item.id}
                      p={3}
                      borderBottom="1px solid #eee"
                      _hover={{
                        bg: getCurrentColor(
                          colorMode,
                          "gray.100",
                          "grayC.2000",
                        ),
                        cursor: "pointer",
                      }}
                    >
                      <Flex gap={4}>
                        <Image
                          src={item.images?.[0]?.uri || "/placeholder.svg"}
                          alt={getDisplayTitle(item)}
                          maxW={"72px"}
                          maxH={"72px"}
                          minW={"72px"}
                          minH={"72px"}
                          borderRadius={"8px"}
                          objectFit={"cover"}
                        />
                        <Flex
                          direction={"column"}
                          gap={2}
                          justifyContent={"center"}
                        >
                          <Link
                            to={item.uri}
                            as={RouterLink}
                            onClick={() => handleResultClick(item, index)}
                          >
                            {getDisplayTitle(item)}
                          </Link>
                          <Wrap spacing={2}>
                            {getAttr(item, "caliber") && (
                              <WrapItem>
                                <Tag
                                  size="sm"
                                  colorScheme="blue"
                                  variant="subtle"
                                >
                                  {getAttr(item, "caliber")}
                                </Tag>
                              </WrapItem>
                            )}
                            {getAttr(item, "bullet_weight_text") && (
                              <WrapItem>
                                <Tag
                                  size="sm"
                                  colorScheme="green"
                                  variant="subtle"
                                >
                                  {getAttr(item, "bullet_weight_text")}
                                </Tag>
                              </WrapItem>
                            )}
                            {getAttr(item, "bullet_type_s") && (
                              <WrapItem>
                                <Tag
                                  size="sm"
                                  colorScheme="purple"
                                  variant="subtle"
                                >
                                  {getAttr(item, "bullet_type_s")}
                                </Tag>
                              </WrapItem>
                            )}
                            {getAttr(item, "material") && (
                              <WrapItem>
                                <Tag
                                  size="sm"
                                  colorScheme="orange"
                                  variant="subtle"
                                >
                                  {getAttr(item, "material")}
                                </Tag>
                              </WrapItem>
                            )}
                          </Wrap>
                        </Flex>
                      </Flex>
                    </ListItem>
                  ))
                )}
              </List>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  return isMobile ? MobileSearch : DesktopSearch;
}
